import { AlertsData, AlertsMetaData } from "./PanelDef";
import { PanelViewComponent } from "../PanelDef";
import React from "react";
import { PaginationProps } from "semantic-ui-react";
import {
  AlertRule,
  AlertRuleCriticality,
  validateWholeNumber,
} from "../../../../../util";
import { fetchPanelData } from "../../../../../BytebeamClient";
import { beamtoast } from "../../../../common/CustomToast";
import { isEqual, sortBy } from "lodash";
import AlertsComponent, {
  SharedAlertsViewProps,
  AlertsViewContext,
  AlertAggregate,
} from "../../../Alerts/AlertsDashboard/AlertsComponent";

type ViewAlertsState = {
  data?: AlertsData;
  activeTab: string;
  tabList: string[];
  activeIndex: number;
  currentPage: number;
  alertsPerPage: number;
  inputPageNumber: number;
  totalPages: number;
  loading: boolean;
};

export const colorMap: Record<AlertRuleCriticality, string> = {
  critical: "#c53f4f",
  warning: "#9E9335",
  info: "#6D95D2",
};

export class ViewAlerts extends PanelViewComponent<
  AlertsMetaData,
  AlertsData,
  ViewAlertsState
> {
  state = {} as ViewAlertsState;

  constructor(props) {
    super(props);
    this.state = {
      activeTab: "All Alerts",
      activeIndex: 0,
      tabList: [],
      currentPage: 1,
      alertsPerPage: 20,
      inputPageNumber: 0,
      totalPages: 10,
      loading: false,
    };
  }

  /**
   * Get current panel metadata with dynamic parameters for refresh operations
   * This method allows the dashboard to refresh the panel with current state
   * without losing pagination, filtering, or tab selection
   */
  getCurrentPanelMeta(): AlertsMetaData {
    const { tabByAlertGroups, tabList } = this.props.panelMeta;
    const { activeTab, tabList: stateTabList } = this.state;

    let activeAlertRules: string[] = [];

    if (activeTab === "All Alerts") {
      if (tabByAlertGroups) {
        activeAlertRules =
          tabList === "all"
            ? this.props.data?.alertRules?.map((rule) => rule.name)
            : this.props.data?.alertRules
                ?.filter((rule) => this.state.tabList.includes(rule.group))
                .map((rule) => rule.name);
      } else {
        activeAlertRules =
          tabList === "all"
            ? this.props.data?.alertRules?.map((rule) => rule.name)
            : stateTabList;
      }
    } else {
      activeAlertRules = tabByAlertGroups
        ? this.props.data?.alertRules
            ?.filter((rule) => rule.group === activeTab)
            .map((rule) => rule.name)
        : [activeTab];
    }

    return {
      ...this.props.panelMeta,
      alert_rules: activeAlertRules,
    };
  }

  /**
   * Notify parent component of current panel metadata changes
   * This allows the dashboard to refresh with current state
   */
  private notifyParentOfMetadataChange() {
    // Check if parent has a method to update panel metadata
    if (this.props.onPanelMetadataChange) {
      this.props.onPanelMetadataChange(
        this.props.panelMeta.id,
        this.getCurrentPanelMeta()
      );
    }
  }

  /**
   * - alerts: paginated alerts for current tab/page
   * - alertRules: all alert rules
   * - summary: complete summary for ALL tabs (for badge counts)
   * - totalCount: total count for current filter
   */
  async refreshData(
    pageNumber: number,
    alertsPerPage: number,
    tabName: string,
    isInitialLoad: boolean = false
  ) {
    try {
      this.setState({ loading: true });

      const { tabByAlertGroups, tabList } = this.props.panelMeta;

      const alertArray: string[] = tabList === "all" ? [] : this.state.tabList;
      const filter = tabByAlertGroups
        ? { alert_groups: alertArray }
        : { alert_names: alertArray };

      let activeAlertRules: string[] = [];

      if (tabName === "All Alerts") {
        if (tabByAlertGroups) {
          activeAlertRules =
            tabList === "all"
              ? this.props.data?.alertRules?.map((rule) => rule.name)
              : this.props.data?.alertRules
                  ?.filter((rule) => this.state.tabList.includes(rule.group))
                  .map((rule) => rule.name);
        } else {
          activeAlertRules =
            tabList === "all"
              ? this.props.data?.alertRules?.map((rule) => rule.name)
              : this.state.tabList;
        }
      } else {
        activeAlertRules = tabByAlertGroups
          ? this.props.data?.alertRules
              ?.filter((rule) => rule.group === tabName)
              .map((rule) => rule.name)
          : [tabName];
      }

      const panelMeta: AlertsMetaData = {
        ...this.props.panelMeta,
        page: pageNumber,
        limit: alertsPerPage,
        alert_rules: activeAlertRules,
        ...(alertArray.length > 0 ? filter : {}),
      };

      const response = await fetchPanelData(
        this.props.dashboardId,
        panelMeta.id,
        [panelMeta],
        this.props.fetchParams,
        null,
        null
      );

      const responseData = response[0].data;

      // smart state update: preserve summary/alertRules on tab changes
      if (isInitialLoad || !this.state.data) {
        // Initial load: use complete response data
        this.setState({
          data: responseData,
          currentPage: pageNumber,
          totalPages: Math.ceil(responseData.totalCount / alertsPerPage),
          loading: false,
        });
      } else {
        // Tab change: preserve summary and alertRules, only update alerts
        this.setState({
          data: {
            ...this.state.data, // Keep existing data structure
            alerts: responseData.alerts, // Update alerts for current tab
            totalCount: responseData.totalCount, // update total count for pagination
            // Keep existing summary and alertRules unchanged
            summary: this.state.data.summary,
            alertRules: this.state.data.alertRules,
          },
          currentPage: pageNumber,
          totalPages: Math.ceil(responseData.totalCount / alertsPerPage),
          loading: false,
        });
      }

      // Notify parent of metadata change after state update
      this.notifyParentOfMetadataChange();
    } catch (error) {
      console.error("Error occurred in fetchPanelData", error);
      this.setState({ loading: false });
    }
  }

  handlePaginationInputChange = (event) => {
    const newValue = event.target.value;
    this.setState({ inputPageNumber: Number(newValue) });
  };

  handlePaginationInputKeyDown = (event, totalPages: number) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      if (validateWholeNumber(this.state.inputPageNumber.toString())) {
        this.handlePaginationChange(event, {
          activePage:
            this.state.inputPageNumber && this.state.inputPageNumber > 0
              ? this.state.inputPageNumber > totalPages
                ? (totalPages as number)
                : this.state.inputPageNumber
              : 1,
          totalPages: totalPages,
        });
        this.setState({ inputPageNumber: 0 });
      } else {
        beamtoast.error("Please enter whole number for jump to page.");
      }
    }
  };

  //Pagination change - preserve summary, only update alerts for new page

  async handlePaginationChange(
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) {
    if (data.activePage) {
      this.refreshData(
        data.activePage as number,
        this.state.alertsPerPage,
        this.state.activeTab,
        false // Not initial load
      );
    }
  }

  //Alerts per page change - preserve summary, only update alerts with new limit

  changeAlertsPerPage = (e, data) => {
    try {
      this.setState({
        alertsPerPage: parseInt(data.value),
        currentPage: 1,
      });
      //Per-page change: preserve existing summary/alertRules
      this.refreshData(1, data.value, this.state.activeTab, false);
    } catch (e) {
      beamtoast.error("Failed to change number of alerts per page");
      console.error("Error in changeAlertsPerPage: ", e);
    }
  };

  //tab change - preserve summary and alertRules, only update alerts

  handleTabChange = (tabName: string) => {
    this.setState({
      activeTab: tabName,
      currentPage: 1, // Reset to page 1 when switching tabs
    });

    //Tab change: preserve existing summary/alertRules
    this.refreshData(1, this.state.alertsPerPage, tabName, false);
  };

  //refresh handler - uses isInitialLoad flag

  handleRefreshData = (
    pageNumber: number,
    alertsPerPage: number,
    tabName: string,
    isInitialLoad: boolean = false
  ) => {
    this.refreshData(pageNumber, alertsPerPage, tabName, isInitialLoad);
  };

  fetchAlertTabs(alertRulesData: AlertRule[] = []) {
    let alertRules = alertRulesData ?? [];

    if (this.props.panelMeta.tabByAlertGroups) {
      // Group by alert groups
      this.setState({
        tabList: Array.from(new Set(alertRules.map((rule) => rule.group))),
      });
    } else {
      // Group by individual rule names
      this.setState({
        tabList: [...alertRules.map((rule) => rule.name)],
      });
    }
  }

  areTabListsEqual = (
    prevTabList: string[] | "all",
    newTabList: string[] | "all"
  ) => {
    if (Array.isArray(prevTabList) && Array.isArray(newTabList)) {
      return isEqual(sortBy(prevTabList), sortBy(newTabList));
    }
    return isEqual(prevTabList, newTabList);
  };

  hasAlertsChanged(
    prevAlertRules: AlertRule[],
    currentAlertRules: AlertRule[]
  ) {
    if (prevAlertRules?.length !== currentAlertRules?.length) {
      return true;
    }

    const prevNames = prevAlertRules?.map((alert) => alert?.name);
    const currentNames = currentAlertRules?.map((alert) => alert?.name);
    const prevGroups = new Set(prevAlertRules?.map((alert) => alert?.group));
    const currentGroups = new Set(
      currentAlertRules?.map((alert) => alert?.group)
    );

    return (
      !isEqual(prevNames, currentNames) || !isEqual(prevGroups, currentGroups)
    );
  }

  componentDidMount(): void {
    const data = this.state.data || this.props.data;

    // Initialize tab configuration
    if (
      this.props.panelMeta.tabList === "all" ||
      this.props.panelMeta.tabList === undefined
    ) {
      this.fetchAlertTabs(data.alertRules);
    } else {
      this.setState({ tabList: this.props.panelMeta.tabList });
    }

    // Set initial data and pagination
    this.setState({
      data: this.props.data,
      totalPages: Math.ceil(data.totalCount / this.state.alertsPerPage),
    });

    //initial load to get ALL data for complete summary
    setTimeout(() => {
      this.refreshData(1, this.state.alertsPerPage, "All Alerts", true);
    }, 100);
  }

  componentDidUpdate(prevProps, prevState) {
    const { panelMeta, data, timeRange } = this.props;
    const { tabbedView, tabList, tabByAlertGroups } = panelMeta;
    const currentData = this.state.data;

    // Handle alert rules changes (new rules added/removed)
    if (
      this.hasAlertsChanged(
        prevProps.data.alertRules,
        this.props.data.alertRules
      )
    ) {
      this.fetchAlertTabs(data.alertRules);
      this.setState({ currentPage: 1, activeIndex: 0 });
      // Refresh with initial load to get complete data
      this.refreshData(1, this.state.alertsPerPage, "All Alerts", true);
    }

    // Handle time range changes from dashboard
    if (prevProps.timeRange !== timeRange) {
      this.setState({ data, currentPage: 1, activeIndex: 0 });
      // Refresh with initial load to get complete data
      this.refreshData(1, this.state.alertsPerPage, "All Alerts", true);
    }

    // Handle data updates
    if (!isEqual(prevProps.data, data)) {
      this.setState({ data });
    }

    // Handle tabbed view mode changes
    if (prevProps.panelMeta.tabbedView !== tabbedView && tabbedView) {
      this.setState({
        activeTab: "All Alerts",
        activeIndex: 0,
        currentPage: 1,
      });
      // Refresh with initial load to get complete data
      this.refreshData(1, this.state.alertsPerPage, "All Alerts", true);
    }

    // Handle tab configuration changes
    if (
      !this.areTabListsEqual(prevProps.panelMeta.tabList, tabList) ||
      prevProps.panelMeta.tabByAlertGroups !== tabByAlertGroups
    ) {
      this.setState({ activeIndex: 0, currentPage: 1 });

      if (tabList === "all") {
        this.fetchAlertTabs(data.alertRules);
      } else {
        this.setState({ tabList });
      }
      // Refresh with initial load to get complete data
      this.refreshData(1, this.state.alertsPerPage, "All Alerts", true);
    }

    // Handle total count changes for pagination
    const prevTotalCount = prevProps?.data?.totalCount;
    const currentTotalCount = data.totalCount;
    const prevStateTotalCount = prevState?.data?.totalCount;
    const currentStateTotalCount = this.state?.data?.totalCount;

    if (
      currentData &&
      (prevTotalCount !== currentTotalCount ||
        prevStateTotalCount !== currentStateTotalCount)
    ) {
      this.setState({
        totalPages: Math.ceil(
          currentData.totalCount / this.state.alertsPerPage
        ),
      });
    }

    // Notify parent of metadata changes when state changes that affect panel metadata
    if (
      prevState.activeTab !== this.state.activeTab ||
      prevState.currentPage !== this.state.currentPage ||
      prevState.alertsPerPage !== this.state.alertsPerPage ||
      !isEqual(prevState.tabList, this.state.tabList)
    ) {
      this.notifyParentOfMetadataChange();
    }
  }

  render() {
    const { panelMeta, dashboards, tables } = this.props;
    const {
      tabbedView = false,
      tabByAlertGroups = false,
      aggregates = [],
      tabList,
    } = panelMeta;
    const data = this.state.data || this.props.data;

    if (!data) {
      return <div>Loading alerts...</div>;
    }

    // Convert aggregates to standardized format
    const formattedAggregates: AlertAggregate[] = aggregates.map((agg) => ({
      name: agg.name,
      table: agg.table,
      column: agg.column,
      aggregator: agg.aggregator,
    }));

    //filter data based on panel configuration
    let filteredData = data;
    let filteredAlertGroups = tabByAlertGroups
      ? this.state.tabList
      : Array.from(new Set(data.alertRules?.map((rule) => rule.group) || []));

    // Apply filtering based on tabList configuration
    if (
      tabList !== "all" &&
      Array.isArray(this.state.tabList) &&
      this.state.tabList.length > 0
    ) {
      filteredData = {
        ...data,
        // Filter alerts based on configured rules/groups
        alerts:
          data.alerts?.filter((alert) => {
            if (tabByAlertGroups) {
              const rule = data.alertRules?.find(
                (r) => r.id === alert.alert_rule.id
              );
              return rule && this.state.tabList.includes(rule.group);
            } else {
              return this.state.tabList.includes(alert.alert_rule.name);
            }
          }) || [],
        // Filter alertRules based on configured rules/groups
        alertRules:
          data.alertRules?.filter((rule) => {
            if (tabByAlertGroups) {
              return this.state.tabList.includes(rule.group);
            } else {
              return this.state.tabList.includes(rule.name);
            }
          }) || [],
        // Filter summary to only include configured rules/groups
        summary: Object.fromEntries(
          Object.entries(data.summary || {})
            .filter(([groupName, rules]) => {
              if (tabByAlertGroups) {
                return this.state.tabList.includes(groupName);
              } else {
                // For rule-based filtering, check if any rule in this group is in tabList
                return Object.keys(rules).some((ruleName) =>
                  this.state.tabList.includes(ruleName)
                );
              }
            })
            .map(([groupName, rules]) => {
              if (tabByAlertGroups) {
                return [groupName, rules];
              } else {
                // For rule-based filtering, only include rules that are in tabList
                const filteredRules = Object.fromEntries(
                  Object.entries(rules).filter(([ruleName]) =>
                    this.state.tabList.includes(ruleName)
                  )
                );
                return [groupName, filteredRules];
              }
            })
        ),
        totalCount: data.totalCount, // Keep original total count for pagination
      };

      // Update filtered alert groups
      if (tabByAlertGroups) {
        filteredAlertGroups = this.state.tabList;
      } else {
        filteredAlertGroups = Array.from(
          new Set(filteredData.alertRules?.map((rule) => rule.group) || [])
        );
      }
    }

    // Prepare shared props for AlertsComponent
    const sharedProps: SharedAlertsViewProps = {
      data: filteredData,
      loading: this.state.loading,
      alertGroups: filteredAlertGroups,
      aggregates: formattedAggregates,
      groupByAlertGroups: tabByAlertGroups,
      activePage: this.state.currentPage,
      setActivePage: (page: number) => this.setState({ currentPage: page }),
      totalPages: this.state.totalPages,
      alertsPerPage: this.state.alertsPerPage,
      setAlertsPerPage: (perPage: number) =>
        this.setState({ alertsPerPage: perPage }),
      changeAlertsPerPage: this.changeAlertsPerPage,
      handlePageChange: this.handlePaginationChange.bind(this),
      activeTab: this.state.activeTab,
      setActiveTab: (tab: string) => this.setState({ activeTab: tab }),
      dashboards,
      selectedFilters: [], // Panel context: no filters
      tables,
      tabbedView,
    };

    // Prepare panel context
    const context: AlertsViewContext = {
      isPanel: true,
      panelMeta,
      dashboardId: this.props.dashboardId,
      fetchParams: this.props.fetchParams,
    };

    // Renderd using AlertsComponent
    return (
      <AlertsComponent
        {...sharedProps}
        context={context}
        onTabChange={this.handleTabChange}
        onRefreshData={this.handleRefreshData}
      />
    );
  }
}
